import { HubConnectionState } from '@microsoft/signalr';

import { parseQuery } from '../../core/helpers/parseQuery';
import { PopupTypes } from '../../core/constants/popupTypes';
import { bonusTypesEnum } from '../../core/constants/bonusTypes';
import { ValidationType } from '../../core/constants/validationType';
import { NotificationsType } from '../../core/constants/notificationsType';
import { DefaultQueryParams } from '../../core/constants/defaultQueryParams';
import { launcherErrorCodes } from '../../core/constants/launcherErrorCodes';
import { TournamentMoneyType } from '../../core/constants/tournamentMoneyType';
import { handleErrorCodesPostMessage } from '../../core/helpers/handleErrorCodesPostMessage';

import { gameStore } from '../../store/gameStore';
import { gambleStore } from '../../store/gambleStore';
import { winBarStore } from '../../store/winBarStore';
import { loadingStore } from '../../store/loadingStore';
import { jackPotStore } from '../../store/jackPotStore';
import { freeBetStore } from '../../store/freeBetStore';
import { userInfoStore } from '../../store/userInfoStore';
import { winPopupStore } from '../../store/winPopupStore';
import { tournamentStore } from '../../store/tournamentStore';
import { bettingPartStore } from '../../store/bettingPartStore';
import { applicationStore } from '../../store/applicationStore';
import { userSettingsStore } from '../../store/userSettingsStore';
import { notificationStore } from '../../store/notificationStore';
import { connectionPopupStore } from '../../store/connectionPopupStore';

import { gameDrawer } from '../gameDrawer';
import { iframeService } from '../iframeService';
import { winPopupShownMinOdd } from '../../gameConstants';
import { SoundTypes, soundService } from '../soundService';
import { gameLiveConnection } from '../socketConnection/gameConnection';
import { MAX_USER_INACTIVE_STATE_DURATION } from '../../core/constants/gameConstants';
import { betReplayService } from '../betReplayService';
import { amountContainerInfo } from '../../core/constants/amountContainerInfo';
import { gameGlobalStates } from '../../core/constants/reelConstants';
import { getSecondsDifference } from '../../core/helpers/formatTime';
import { worker } from '../../App';
import { boostedJackpotStore } from '../../store/boostedJackpotStore';

let userActiveStateTimeOut;
let spinTimeOutId = null;
let bigWinPopupTimeOut = null;

class GameService {
  onGetInitialStateResult({
    rate,
    matrix,
    maxOdd,
    balance,
    autobet,
    oddsInfo,
    spinInfo,
    quickBets,
    hasGamble,
    precision,
    hasJackpot,
    validation,
    userFreeBets,
    currencyCode,
    bonusSpinInfo,
    tournamentInfo,
    defaultBetAmount,
    underMaintenance,
    boostedJackpotInfo,
  }) {
    if (validation !== ValidationType.Success) return;
    if (underMaintenance) {
      notificationStore.showNotification({
        type: NotificationsType.Maintenance,
        notificationInfo: underMaintenance,
      });
    }
    tournamentStore.setTournamentId(null);
    const initialMatrix = spinInfo && spinInfo.result ? spinInfo.result.expandMatrix : matrix;
    userInfoStore.setCurrencySymbolOrCode(currencyCode);
    gameStore.setGameInitialMatrix(initialMatrix);
    gameStore.setNoWinMatrix(matrix);
    gameStore.setHasAutoBet(autobet);
    jackPotStore.setHasJackPot(hasJackpot);
    gambleStore.setHasGamble(hasGamble);
    if (!userInfoStore.isReplay) {
      gameStore.setOddsInfo(oddsInfo);
    }
    bettingPartStore.setQuickBets(quickBets);
    bettingPartStore.updateSelectedQuickBet(defaultBetAmount);
    if (!userInfoStore.isReplay) {
      userInfoStore.setUserConfigs({
        rate,
        balance,
        precision,
        limits: {
          maxBet: quickBets[quickBets.length - 1],
          minBet: quickBets[0],
          maxOdd,
          boostedJackpotBetAmount: quickBets.find((el) => el >= boostedJackpotInfo?.betAmount),
        },
      });
    }
    if (bonusSpinInfo) {
      gameStore.setGameGlobalState(gameGlobalStates.bonus);
      if (bonusSpinInfo.bonusType === bonusTypesEnum.boostedJackpot) {
        boostedJackpotStore.setIsWheel(true);
        gameStore.setIsSpinStart(true);
        this.sendWorker(getSecondsDifference(bonusSpinInfo.expireTime));
      }
      if (bonusSpinInfo.bonusType === bonusTypesEnum.jackpot) {
        if (userSettingsStore.isSoundOn) {
          soundService.play(SoundTypes.JackpotBackgroundMusic, { loop: true });
        }
        const {
          bonusData: { jackpotResult, expireDate },
        } = bonusSpinInfo;
        jackpotResult
          ? jackPotStore.setJackPotData(jackpotResult.map((el) => ({ ...el, isEnd: true })))
          : null;
        this.sendWorker(getSecondsDifference(expireDate));
        jackPotStore.setIsShowJackpotScene(true);
        jackPotStore.setInitialBonusSpinInfo(bonusSpinInfo);
      }
    } else {
      soundService.playMusic();
    }
    if (spinInfo) {
      const { amount, result, winAmount } = spinInfo;
      winBarStore.setWinAmount(winAmount);
      bettingPartStore.updateSelectedQuickBet(amount);
      if (spinInfo.result) {
        gameStore.setSpinResult(result);
        gameStore.setIsSpinStart(true);
      }
    }
    if (boostedJackpotInfo) {
      this.boostedJackpotInitialState(boostedJackpotInfo, quickBets);
    }
    if (userFreeBets && userFreeBets.length) {
      freeBetStore.showFreeBetInfo(userFreeBets);
      winBarStore.setTotalWinAmount(userFreeBets[0].totalWin);
      winBarStore.updateShowAmountContainerInfo(amountContainerInfo.totalWinContainer, true);
    }
    if (tournamentInfo) {
      tournamentStore.setTournamentId(tournamentInfo.tournamentId);
      if (tournamentInfo.moneyType === TournamentMoneyType.FreeBet && tournamentInfo.freeBetCount) {
        this.tournamentFreeBetHandler(tournamentInfo);
      } else if (tournamentInfo.moneyType === TournamentMoneyType.PgCoin) {
        userInfoStore.setUserBalance(tournamentInfo.pgCoinBalance);
      }
    }
    loadingStore.setHasGameInitialState(true);
  }

  handleBoostedJackpotExpirationInfoResult() {
    gameStore.setIsSpinStart(false);
    boostedJackpotStore.setIsBoostedJackpot(false);
    userSettingsStore.setIsBoostedJackpotInitialPopup(false);
  }

  boostedJackpotInitialState(
    {
      id,
      endTime,
      lastWin,
      poolSize,
      winsCount,
      betAmount,
      largestWin,
      lastWinDate,
      wheelValues,
      leftPoolSize,
      leftWinsCount,
      largestWinDate,
    },
    quickBets = null
  ) {
    const { isBoostedJackpotInitialPopup } = userSettingsStore;
    const {
      setWheelValues,
      setWinningCount,
      setLastWinnerDate,
      setPrizePoolAmount,
      setBoostedBetAmount,
      setWinningCountLeft,
      setLastWinnerAmount,
      setLargestWinnerDate,
      setLargestWinnerAmount,
      setPrizePoolLeftAmount,
      setBoostedJackpotEndTime,
      setBoostedInitialBetAmount,
    } = boostedJackpotStore;

    boostedJackpotStore.setIsBoostedJackpot(true);
    userSettingsStore.updateNewBoostedJackpotById(id);

    setWinningCount(winsCount);
    setWheelValues(wheelValues);
    setPrizePoolAmount(poolSize);
    setLastWinnerAmount(lastWin);
    setLastWinnerDate(lastWinDate);
    setBoostedJackpotEndTime(endTime);
    setLargestWinnerAmount(largestWin);
    setWinningCountLeft(leftWinsCount);
    setLargestWinnerDate(largestWinDate);
    setPrizePoolLeftAmount(leftPoolSize);
    setBoostedInitialBetAmount(betAmount);
    if (!isBoostedJackpotInitialPopup && quickBets) {
      const validQuickBet = quickBets.find((el) => el >= betAmount);
      setBoostedBetAmount(validQuickBet);
    }
    if (userInfoStore.isReplay) {
      userSettingsStore.setIsBoostedJackpotInitialPopup(false);
    }
  }

  handleSpinResult({
    odd,
    result,
    balance,
    bonusType,
    winAmount,
    gambleInfo,
    validation,
    freeBetInfo,
    tournamentInfo,
  }) {
    if (validation === ValidationType.Success) {
      clearTimeout(spinTimeOutId);
      this.checkUserActiveStat();
      if (winBarStore.totalWinAmount || winBarStore.lastWinAmount) {
        winBarStore.updateShowAmountContainerInfo(
          freeBetStore.currentFreeBet
            ? amountContainerInfo.totalWinContainer
            : amountContainerInfo.lastWinContainer,
          true
        );
      }
      if (result.additional?.wins?.length > 0) {
        gameStore.setHasWin(true);
      } else {
        gameStore.setHasWin(false);
      }
      const res = result.length ? { matrix: result } : result;

      gameStore.setSpinResult({
        ...res,
        winAmount,
        gambleInfo,
        freeBetInfo,
        tournamentInfo,
      });
      if (freeBetInfo && (freeBetInfo.reminingSpinCount || !winAmount)) {
        winBarStore.setTotalWinAmount(freeBetInfo.totalWin);
        freeBetStore.setCurrentFreeBet({
          ...freeBetStore.currentFreeBet,
          freeBetInfo,
          remainingCount: freeBetInfo.reminingSpinCount,
        });
      }
      if (freeBetInfo && !freeBetInfo.reminingSpinCount) {
        this.resetAutoBetState();
      }
      winBarStore.setWinAmount(0);
      winBarStore.setWinLine(null);

      if (tournamentInfo && tournamentInfo.moneyType === TournamentMoneyType.FreeBet) {
        freeBetStore.setCurrentFreeBet({
          ...freeBetStore.currentFreeBet,
          remainingCount: tournamentInfo.currentFreeBetCount,
        });
      }
      applicationStore.setBonusType(bonusType);
      if (!freeBetInfo) userInfoStore.decreaseBalance(bettingPartStore.selectedQuickBet, balance);
      gameStore.setOdd(odd);
      if (gameStore.isNeedGetBalance && !applicationStore.isDemo) {
        this.getBalanceDependTournament();
      }
      if (bettingPartStore.isAutoBetStart && bettingPartStore.selectedAutoBetCount) {
        bettingPartStore.setSelectedAutoBetCount(bettingPartStore.selectedAutoBetCount - 1);
      }
    } else {
      gameStore.setHasWin(false);
      this.errorMessageHandler(validation);
      this.resetAutoBetState();
      gameStore.setSpinResult({
        matrix: gameStore.noWinMatrix,
        winAmount: 0,
        additional: { wins: [] },
      });
    }
    // setTimeout(
    //   () => {
    gameDrawer.stopSpinAnimation();
    //   },
    //   bettingPartStore.gameSpeedType === GAME_SPEED_TYPES.turbo ? 0 : 200
    // );
  }

  tournamentFreeBetHandler(tournamentInfo) {
    const tournamentFreeBetModel = {
      remainingCount: tournamentInfo.freeBetCount,
      amount: tournamentInfo.freeBetPGCoinAmount,
    };
    freeBetStore.setCurrentFreeBet(tournamentFreeBetModel);
    freeBetStore.setFreeBetList([tournamentFreeBetModel]);
    freeBetStore.setIsShowFreeBetPopup(true);
    freeBetStore.setIsFreeBetEnd(false);
  }

  getBalanceDependTournament() {
    const {
      [DefaultQueryParams.GameId]: gameId,
      [DefaultQueryParams.TournamentId]: tournamentId,
      [DefaultQueryParams.PartnerId]: partnerId,
    } = parseQuery(window.location.search);
    if (tournamentStore.tournamentId) {
      gameLiveConnection.getTournamentBalance({ gameId, partnerId, tournamentId });
    } else if (!userInfoStore.isReplay) {
      setTimeout(() => {
        gameLiveConnection.getBalance();
      }, 350);
    }
  }

  resetAutoBetState() {
    bettingPartStore.setIsAutoBetStart(false);
    bettingPartStore.setSelectedAutoBetCount(null);
    bettingPartStore.setIsOpenAutoBetPopup(false);
  }

  handleFreeBetEnd() {
    if (
      freeBetStore.currentFreeBet &&
      !freeBetStore.currentFreeBet.remainingCount &&
      !tournamentStore.tournamentId
    ) {
      if (gambleStore.gambleInfo) {
        this.doSpinCollect();
      }
      freeBetStore.freeBetEnd();
    }
  }

  handleTournamentEnd() {
    if (
      tournamentStore.tournamentId &&
      userInfoStore.balance < bettingPartStore.quickBets[0] &&
      !freeBetStore.currentFreeBet?.remainingCount
    ) {
      iframeService.sendTournamentFinished();
      this.resetAutoBetState();
      if (gambleStore.gambleInfo) {
        this.doSpinCollect();
      }
    }
  }

  spinFinished({ odd, gambleInfo, winAmount }) {
    if (notificationStore.isMaintenance) {
      iframeService.reloadFrame();
    }
    if (
      applicationStore.bonusType === bonusTypesEnum.jackpot ||
      gameStore.replayData?.bonus?.bonusType === bonusTypesEnum.jackpot
    ) {
      this.handleStartJackpotScene();
      return;
    }

    if (
      applicationStore.bonusType === bonusTypesEnum.boostedJackpot ||
      gameStore.replayData?.bonus?.bonusType === bonusTypesEnum.boostedJackpot
    ) {
      this.handleStartBoostedJackpotWheel();
      return;
    }

    if (winAmount && !gambleInfo) {
      this.balanceUpdate();
    }
    if (winBarStore.winAmount && !gambleInfo) {
      winBarStore.setLastWinAmount(winBarStore.winAmount);
    }
    if (odd >= winPopupShownMinOdd && !gameStore.isReelsRotating) {
      bigWinPopupTimeOut = setTimeout(
        () => {
          applicationStore.setIsShowMobileBigWinLayer(true);
          if (!freeBetStore.currentFreeBet) winBarStore.hideAmountContainer();
          winBarStore.setWinAmount(winAmount);
          gameStore.setOdd(0);
          winPopupStore.winPopupOpen(odd);
        },
        winAmount ? 0 : 2000
      );
    } else {
      this.handleAutoSpin({ gambleInfo, winAmount });
    }
    if (userInfoStore.isReplay) {
      if (odd >= winPopupShownMinOdd) {
        setTimeout(() => {
          betReplayService.sendReplayEventEnd();
        }, 2000);
      } else {
        betReplayService.sendReplayEventEnd();
      }
    }
  }

  handleStartBoostedJackpotWheel() {
    gameStore.setGameGlobalState(gameGlobalStates.bonus);
    boostedJackpotStore.setIsWheel(true);
    gameStore.setIsSpinStart(true);
    gameStore.setHasWin(true);
    this.resetAutoBetState();
  }

  handleStartJackpotScene() {
    gameStore.setGameGlobalState(gameGlobalStates.bonus);
    gameStore.setIsSpinStart(true);
    gameStore.setHasWin(true);
    gameDrawer.handleJackpotWin().then((resolverCount) => {
      jackPotStore.setIsShowJackpot(true);
      // eslint-disable-next-line no-unused-vars
      resolverCount = 0;
    });
  }

  handleAutoSpin() {
    this.handleFreeBetEnd();
    this.handleTournamentEnd();
    if (bettingPartStore.isAutoBetStart && bettingPartStore.selectedAutoBetCount) {
      this.doSpin();
    } else if (bettingPartStore.isAutoBetStart && !bettingPartStore.selectedAutoBetCount) {
      this.resetAutoBetState();
    }
  }

  doSpin() {
    if (gambleStore.gambleInfo) {
      if (gambleStore.isAllowDoGambleAction) {
        gambleStore.setIsAllowDoGambleAction(false);
        gambleStore.setGambleInfo(null);
        this.doSpinCollect();
        gameStore.setIsNeedSpinAction(true);
      }
      return;
    }
    if (
      (gameStore.gameGlobalState !== gameGlobalStates.none || freeBetStore.isShowFreeBetPopup) &&
      !(
        gameStore.gameGlobalState === gameGlobalStates.show &&
        gameStore.winEnded &&
        bettingPartStore.isAutoBetStart
      )
    ) {
      return;
    }
    gameStore.setGameGlobalState(gameGlobalStates.waiting);
    gameStore.setIsSpinStart(true);
    clearTimeout(bigWinPopupTimeOut);
    bettingPartStore.setIsShowQuickBetsListPopup(false);
    const { [DefaultQueryParams.PartnerId]: partnerId, [DefaultQueryParams.GameId]: gameId } =
      parseQuery(window.location.search);
    const spinModel = {
      gameId: gameId,
      spinData: false,
      partnerId: partnerId,
      amount: bettingPartStore.selectedQuickBet,
      tournamentId: tournamentStore.tournamentId,
      freeBetId: freeBetStore.currentFreeBet && freeBetStore.currentFreeBet.id,
    };
    gameDrawer.resetScatterCount();
    gambleStore.resetGambleState();
    notificationStore.hideNotification();
    soundService.stopAllSounds();
    winPopupStore.winPopupClose();
    applicationStore.setIsShowBlackLayer(false);
    applicationStore.setIsShowMobileBigWinLayer(false);
    gameDrawer.skipWinAnimation();
    gameStore.setIsNeedGetBalance(!!winBarStore.winAmount);
    this.handleFreeBetEnd();
    gameDrawer.startSpinAnimation();
    clearTimeout(bigWinPopupTimeOut);
    soundService.play(SoundTypes.SpinButton);
    winBarStore.setLastWinAmount(winBarStore.winAmount || winBarStore.lastWinAmount);
    if (applicationStore.isDemo && bettingPartStore.selectedQuickBet > userInfoStore.balance) {
      this.errorMessageHandler(ValidationType.NotEnoughMoney);
      this.resetAutoBetState();
      gameStore.setSpinResult({ matrix: gameStore.noWinMatrix });
      setTimeout(() => {
        gameDrawer.stopSpinAnimation();
      }, 200);
    } else {
      spinTimeOutId = setTimeout(() => {
        this.handleConnectionLost();
      }, 7000);
      gameLiveConnection.doSpin(spinModel);
    }
  }

  handleConnectionLost() {
    if (gameLiveConnection.connection.state === HubConnectionState.Disconnected) {
      this.sendErrorCodeToLauncher();
      gameStore.setSpinResult({ matrix: gameStore.noWinMatrix });
      setTimeout(() => {
        gameDrawer.stopSpinAnimation();
      }, 200);
    }
  }

  doSpinCollect() {
    const { [DefaultQueryParams.GameId]: gameId } = parseQuery(window.location.search);
    gameLiveConnection.doSpinCollect(gameId);
  }

  sendGambleNextCard(card) {
    const { [DefaultQueryParams.GameId]: gameId } = parseQuery(window.location.search);
    const model = {
      card,
      gameId,
    };
    gameLiveConnection.sendGamble(model);
  }

  handleGambleResult({
    isWin,
    gambleCard,
    gambleFinished,
    totalWinAmount,
    gambleAttemptsCount,
    gambleAmountsInfo,
    validation,
  }) {
    if (validation === ValidationType.Success) {
      gambleStore.updateGambleHistoryCards(gambleCard);
      gambleStore.setOpenedCard(gambleCard);
      const gambleAmountsState = isWin
        ? [gambleAmountsInfo]
        : gambleStore.gambleInfo.gambleAmountsInfo;

      gambleStore.updateGambleInfo({ gambleAttemptsCount });

      if (isWin) {
        gambleStore.updateGambleInfo({
          gambleAmountsInfo: gambleAmountsState,
        });
      }

      if (gambleFinished) {
        winBarStore.setWinAmount(0);
        winBarStore.setWinLine(null);
        winBarStore.updateShowAmountContainerInfo(
          amountContainerInfo.winContainer,
          !!winBarStore.lastWinAmount
        );
        if (isWin) {
          winBarStore.updateShowAmountContainerInfo(amountContainerInfo.winContainer, true);
          winBarStore.setLastWinAmount(totalWinAmount);
          winBarStore.setWinAmount(totalWinAmount);
          gameDrawer.animateWinAmount({ value: totalWinAmount });
          gambleStore.setTotalWinAmount(totalWinAmount);
          this.balanceUpdate();
        } else {
          const container = freeBetStore.currentFreeBet
            ? amountContainerInfo.totalWinContainer
            : winBarStore.lastWinAmount
            ? amountContainerInfo.lastWinContainer
            : null;

          if (container) {
            winBarStore.updateShowAmountContainerInfo(container, true);
          }
          gambleStore.setIsGambleLose(true);
        }
        setTimeout(() => {
          gambleStore.resetGambleState();
        }, 2000);
      } else {
        setTimeout(() => {
          gambleStore.setOpenedCard(null);
          gambleStore.setIsAllowDoGambleAction(isWin);
        }, 1500);
      }
    } else {
      gambleStore.setIsAllowDoGambleAction(true);
    }
  }

  getBoostedJackpot() {
    const { [DefaultQueryParams.GameId]: gameId } = parseQuery(window.location.search);
    gameLiveConnection.getBoostedJackpotWin(gameId);
  }

  handleSpinCollectResult({ totalWinAmount, validation }) {
    if (validation === ValidationType.Success) {
      gambleStore.setTotalWinAmount(totalWinAmount);
      winBarStore.setWinAmount(totalWinAmount);
      gameStore.setIsNeedGetBalance(Boolean(totalWinAmount));
      gameDrawer.animateWinAmount({ value: totalWinAmount, duration: 0 });
      this.balanceUpdate();
      gambleStore.resetGambleState();
      winBarStore.setLastWinAmount(totalWinAmount);
    } else {
      this.errorMessageHandler(validation);
      gambleStore.setIsAllowDoGambleAction(true);
      gameStore.setIsSpinStart(false);
    }
    if (gameStore.isNeedSpinAction) {
      gameStore.setIsNeedSpinAction(false);
      gambleStore.resetGambleState();
      this.doSpin();
    }
  }

  errorMessageHandler(validation) {
    if (validation === ValidationType.NotEnoughMoney) {
      connectionPopupStore.open(PopupTypes.INSUFFICIENT_AMOUNT);
    } else {
      notificationStore.showNotification({
        type: NotificationsType.SpinResponseCode,
        notificationInfo: validation,
      });
    }
    this.resetAutoBetState();
  }

  getJackpotSymbol(index) {
    gameLiveConnection.getJackpotSymbol({ index });
  }

  handleJackpotSceneItemResult(data) {
    if (data.validation === ValidationType.Success) {
      if (data.jackpotSceneItemInfo.isWin) {
        jackPotStore.updateJackpotAmounts(data.jackpotSceneItemInfo);
        jackPotStore.setIsAllowDoJackpotAction(false);
        setTimeout(() => {
          jackPotStore.setIsExpiredJackpotCards(true);
          jackPotStore.showRemainingJackpotCards();
        }, 3000);
      }
      jackPotStore.updateJackpotData(data);
    } else {
      jackPotStore.setIsAllowDoJackpotAction(true);
    }
  }

  handleBoostedJackpotWinResult(data) {
    if (data.validationType === ValidationType.Success) {
      const { index, winAmount } = data;
      boostedJackpotStore.setSectorStopIndex(index);
      boostedJackpotStore.setSpinWheelWinAmount(winAmount);
    } else {
      this.sendErrorCodeToLauncher();
      boostedJackpotStore.setSectorStopIndex(null);
    }
  }

  handleBoostedJackpotActivationInfoResult(data) {
    userSettingsStore.setIsBoostedJackpotInitialPopup(false);
    this.boostedJackpotInitialState(data);
  }

  updateDemoBalance = () => {
    const winAmount = gambleStore.gambleInfo ? gambleStore.totalWinAmount : winBarStore.winAmount;
    userInfoStore.increaseBalance(winAmount);
  };

  balanceUpdate() {
    if (applicationStore.isDemo) {
      this.updateDemoBalance();
    } else {
      this.getBalanceDependTournament();
    }
  }

  handleJackpotSceneResult(data) {
    if (data.validation === ValidationType.Success) {
      const winCard = data.jackpotSceneItemInfo.find((item) => item.isWin).jackpotCard;
      jackPotStore.setIsExpiredJackpotCards(true);
      jackPotStore.setWinCard(winCard);
      jackPotStore.updateJackpotData(data.jackpotSceneItemInfo, true);
      jackPotStore.showRemainingJackpotCards(true);
      setTimeout(() => {
        jackPotStore.setIsShowJackPotWinPopup(true);
      }, 3000);
    }
  }

  handleFreeBetAwarded({ freeBets }) {
    const userFreeBets = freeBets?.filter((el) => el.remainingCount);
    freeBetStore.showFreeBetInfo(userFreeBets);
  }

  handleTournamentInfoResult({ tournamentInfo, validation }) {
    if (validation === ValidationType.Success && tournamentInfo) {
      tournamentStore.setTournamentId(tournamentInfo.tournamentId);
      if (tournamentInfo.moneyType === TournamentMoneyType.FreeBet) {
        this.tournamentFreeBetHandler(tournamentInfo);
      } else if (tournamentInfo.moneyType === TournamentMoneyType.PgCoin) {
        userInfoStore.setUserBalance(tournamentInfo.pgCoinBalance);
      }
    }
  }

  getTournamentUpdate() {
    const {
      [DefaultQueryParams.PartnerId]: partnerId,
      [DefaultQueryParams.GameId]: gameId,
      [DefaultQueryParams.TournamentId]: tournamentId,
    } = parseQuery(window.location.search);
    gameLiveConnection.getTournamentInfo({ partnerId, gameId, tournamentId });
  }

  // must be arrow function
  visibilityChangeHandler = () => {
    const state = document.visibilityState;
    if (state === 'hidden') {
      this.visibilityStartTime = Date.now();
    } else if (Date.now() - this.visibilityStartTime > MAX_USER_INACTIVE_STATE_DURATION) {
      this.sendErrorCodeToLauncher();
    }
  };

  // must be arrow function
  checkUserActiveStat = () => {
    clearTimeout(userActiveStateTimeOut);
    userActiveStateTimeOut = setTimeout(() => {
      this.sendErrorCodeToLauncher();
    }, MAX_USER_INACTIVE_STATE_DURATION);
  };

  // must be arrow function
  sendErrorCodeToLauncher = () => {
    const { ConnectionError } = launcherErrorCodes;
    handleErrorCodesPostMessage(ConnectionError);
  };

  countDown({ data }) {
    jackPotStore.setJackPotExpireTime(data);
  }

  sendWorker(time) {
    if (time) {
      worker.addEventListener('message', this.countDown);
      worker.postMessage(time);
    } else {
      worker.removeEventListener('message', this.countDown);
    }
  }

  handleReconnect() {
    this.resetAutoBetState();
    setTimeout(() => {
      if (gameStore.isReelsRotating && !gameStore.isResponseReady) {
        gameStore.setSpinResult({
          matrix: gameStore.noWinMatrix,
          winAmount: 0,
          additional: { wins: [] },
        });
        gameDrawer.stopSpinAnimation();
      }
    }, 5000);
  }
}

export const gameService = new GameService();
